import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuthStore } from '@/stores/auth';
import {
  ArrowRightIcon,
  SparklesIcon,
  CheckCircleIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  LockClosedIcon,
  PlusIcon,
  MinusIcon,
  ClipboardDocumentCheckIcon,
  UserGroupIcon,
  SignalIcon,
  LightBulbIcon,
  ArrowTrendingUpIcon,
  BuildingOffice2Icon,
} from '@heroicons/react/24/outline';

const LandingPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuthStore();
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const features = [
    {
      icon: <ClipboardDocumentCheckIcon className="w-8 h-8" />,
      title: "Structured Hiring Workflows",
      description: "Turn unstructured intake calls into calibrated scorecards. Align recruiters and hiring managers before the first screen.",
      gradient: "from-violet-600 to-purple-600"
    },
    {
      icon: <SignalIcon className="w-8 h-8" />,
      title: "Interview Signal Synthesis",
      description: "Aggregate interviewer feedback into clear, comparable signals. Replace gut-feel debriefs with data-driven hiring decisions.",
      gradient: "from-blue-600 to-cyan-600"
    },
    {
      icon: <UserGroupIcon className="w-8 h-8" />,
      title: "Interviewer Calibration",
      description: "Identify bias patterns, scoring drift, and misaligned expectations across your interview panels before they cost you great hires.",
      gradient: "from-emerald-600 to-teal-600"
    },
    {
      icon: <ChartBarIcon className="w-8 h-8" />,
      title: "Hiring Effectiveness Analytics",
      description: "Track quality-of-hire signals, time-to-decision, and process bottlenecks. Know what's working and what's costing you candidates.",
      gradient: "from-orange-600 to-red-600"
    }
  ];

  const outcomes = [
    { metric: "40%", label: "Faster time-to-decision", description: "Structured workflows eliminate back-and-forth between recruiters and hiring managers" },
    { metric: "3x", label: "Better signal quality", description: "Calibrated scorecards surface what matters instead of who interviews best" },
    { metric: "60%", label: "Less recruiter admin time", description: "Automated intake, debrief synthesis, and candidate communication" },
  ];

  const faqs = [
    {
      question: "How is Joblyn different from our ATS?",
      answer: "Your ATS is a system of record — it tracks where candidates are. Joblyn is a system of intelligence — it improves how your team makes hiring decisions. We integrate with your existing ATS and sit on top of your workflow, not beside it."
    },
    {
      question: "Do we need to change our hiring process to use Joblyn?",
      answer: "No. Joblyn adapts to your existing process and surfaces improvements incrementally. Most teams see value within the first two weeks without changing a single workflow."
    },
    {
      question: "How does Joblyn handle candidate data and privacy?",
      answer: "Enterprise-grade encryption, SOC 2 compliance path, and strict data isolation. Candidate data is used only within your organization's context and is never shared or used to train models across customers."
    },
    {
      question: "What size teams benefit most?",
      answer: "Teams making 10+ hires per quarter see the strongest ROI, but even smaller teams benefit from structured scorecards and interviewer calibration. The value compounds as your hiring volume and panel size grow."
    },
    {
      question: "How do you measure hiring quality?",
      answer: "We track leading indicators — scorecard consistency, interviewer agreement rates, time-to-decision, and candidate experience scores — rather than lagging metrics alone. Over time, we correlate these with retention and performance data you provide."
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.1,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        damping: 12,
        stiffness: 100
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50/30 to-blue-50/30 overflow-hidden">
      {/* Subtle background */}
      <div className="fixed inset-0 opacity-30">
        <div className="absolute inset-0 bg-gradient-to-br from-violet-200 via-purple-200 to-pink-200" />
      </div>

      {/* Navigation */}
      <nav className="relative z-50 backdrop-blur-xl bg-white/70 border-b border-white/20 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center space-x-3"
            >
              <div className="p-2 bg-gradient-to-br from-violet-600 to-purple-600 rounded-xl shadow-lg">
                <SparklesIcon className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent">
                Joblyn
              </span>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center space-x-4"
            >
              {isAuthenticated ? (
                <>
                  <span className="text-gray-600 text-sm">
                    Welcome back, {user?.full_name || user?.email}!
                  </span>
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="px-6 py-2 bg-gradient-to-r from-violet-600 to-purple-600 text-white font-medium rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center"
                  >
                    Go to Dashboard
                    <ArrowRightIcon className="w-4 h-4 ml-2" />
                  </button>
                </>
              ) : (
                <button
                  onClick={() => navigate('/login')}
                  className="px-6 py-2 bg-gradient-to-r from-violet-600 to-purple-600 text-white font-medium rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  Sign In
                </button>
              )}
            </motion.div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative z-10 pt-20 pb-24 px-4">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="max-w-7xl mx-auto text-center"
        >
          <motion.div
            variants={itemVariants}
            className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-amber-100 to-orange-100 rounded-full mb-6 shadow-md"
          >
            <LockClosedIcon className="w-4 h-4 text-amber-600 mr-2" />
            <span className="text-sm font-medium text-amber-800">Invite-Only Early Access</span>
          </motion.div>

          <motion.h1
            variants={itemVariants}
            className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-violet-600 via-purple-600 to-pink-600 bg-clip-text text-transparent"
          >
            Less Noise.
            <br />
            <span className="text-4xl md:text-6xl">Better Hires.</span>
          </motion.h1>

          <motion.p
            variants={itemVariants}
            className="text-xl text-gray-600 mb-10 max-w-3xl mx-auto leading-relaxed"
          >
            Joblyn is the AI-powered hiring intelligence layer that helps your team make
            better decisions — from intake to offer. Structured workflows, calibrated interviews,
            and measurable hiring quality.
          </motion.p>

          <motion.div
            variants={itemVariants}
            className="flex flex-col sm:flex-row gap-4 justify-center mb-6"
          >
            {isAuthenticated ? (
              <button
                onClick={() => navigate('/dashboard')}
                className="group px-8 py-4 bg-gradient-to-r from-violet-600 to-purple-600 text-white font-semibold rounded-2xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-200 flex items-center justify-center"
              >
                Continue to Your Dashboard
                <ArrowRightIcon className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </button>
            ) : (
              <div className="flex flex-col items-center gap-3">
                <button
                  onClick={() => navigate('/login')}
                  className="group px-8 py-4 bg-gradient-to-r from-violet-600 to-purple-600 text-white font-semibold rounded-2xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-200 flex items-center justify-center"
                >
                  Request Early Access
                  <ArrowRightIcon className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </button>
                <p className="text-sm text-gray-500">
                  Already have an account? <button onClick={() => navigate('/login')} className="text-purple-600 font-medium hover:underline">Sign in</button>
                </p>
              </div>
            )}
          </motion.div>
        </motion.div>
      </section>

      {/* Problem Statement */}
      <section className="relative z-10 py-16 px-4 bg-white/40">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">
              Hiring is broken in ways your ATS can't fix
            </h2>
            <div className="grid md:grid-cols-3 gap-8 text-left max-w-4xl mx-auto">
              {[
                {
                  problem: "Misaligned expectations",
                  detail: "Recruiters and hiring managers talk past each other. Intake is informal. Requirements shift mid-search."
                },
                {
                  problem: "Inconsistent evaluation",
                  detail: "Every interviewer scores differently. Debriefs are dominated by whoever speaks loudest, not strongest signal."
                },
                {
                  problem: "No feedback loop",
                  detail: "You know how many hires you made. You don't know which process decisions led to good ones."
                }
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-red-100"
                >
                  <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center mb-3">
                    <span className="text-red-500 font-bold text-lg">!</span>
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">{item.problem}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{item.detail}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="relative z-10 py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent">
              The Intelligence Layer Your Hiring Process Needs
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Joblyn sits on top of your existing tools and makes every step of hiring more structured, more consistent, and more measurable.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5, scale: 1.02 }}
                className="group relative bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 border border-white/20 overflow-hidden"
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-5 transition-opacity`} />

                <div className={`inline-flex p-4 rounded-2xl bg-gradient-to-br ${feature.gradient} text-white mb-4 shadow-lg`}>
                  {feature.icon}
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {feature.title}
                </h3>

                <p className="text-gray-600">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Outcomes / Metrics */}
      <section className="relative z-10 py-20 px-4 bg-white/40">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent">
              Measurable Hiring Outcomes
            </h2>
            <p className="text-xl text-gray-600">
              Teams using structured hiring intelligence see real results
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {outcomes.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="text-center bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-white/20"
              >
                <div className="text-5xl font-bold bg-gradient-to-br from-violet-600 to-purple-600 bg-clip-text text-transparent mb-2">
                  {item.metric}
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{item.label}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="relative z-10 py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent">
              How Joblyn Works
            </h2>
            <p className="text-xl text-gray-600">
              Integrates into your existing hiring stack in minutes
            </p>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-6">
            {[
              {
                step: "01",
                title: "Connect Your Tools",
                description: "Integrate with your ATS, calendar, and communication tools. Joblyn works alongside what you already use."
              },
              {
                step: "02",
                title: "Structure Every Role",
                description: "AI converts intake conversations into calibrated scorecards and aligned hiring criteria automatically."
              },
              {
                step: "03",
                title: "Elevate Every Interview",
                description: "Interviewers get structured guides. Feedback is captured consistently. Signals are synthesized, not lost."
              },
              {
                step: "04",
                title: "Improve Continuously",
                description: "Track decision quality, interviewer calibration, and hiring outcomes. Each hire makes the next one better."
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.12 }}
                className="text-center"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-violet-600 to-purple-600 rounded-2xl text-white text-2xl font-bold mb-4 shadow-lg">
                  {item.step}
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Positioning Statement */}
      <section className="relative z-10 py-16 px-4 bg-white/40">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white/80 backdrop-blur-sm rounded-3xl p-10 shadow-xl border border-white/20"
          >
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div className="p-4">
                <BuildingOffice2Icon className="w-8 h-8 text-gray-400 mx-auto mb-3" />
                <h4 className="font-bold text-gray-400 mb-1">Job Boards</h4>
                <p className="text-sm text-gray-400">Own market liquidity</p>
              </div>
              <div className="p-4">
                <ClipboardDocumentCheckIcon className="w-8 h-8 text-gray-400 mx-auto mb-3" />
                <h4 className="font-bold text-gray-400 mb-1">ATS Platforms</h4>
                <p className="text-sm text-gray-400">Own recordkeeping</p>
              </div>
              <div className="p-4 bg-gradient-to-br from-violet-50 to-purple-50 rounded-2xl border-2 border-violet-200">
                <LightBulbIcon className="w-8 h-8 text-violet-600 mx-auto mb-3" />
                <h4 className="font-bold text-violet-700 mb-1">Joblyn</h4>
                <p className="text-sm text-violet-600 font-medium">Owns decision quality</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="relative z-10 py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent">
              Frequently Asked Questions
            </h2>
          </motion.div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-6 py-6 text-left flex items-center justify-between hover:bg-white/50 transition-colors"
                >
                  <span className="font-semibold text-gray-900 text-lg">{faq.question}</span>
                  {openFaq === index ? (
                    <MinusIcon className="w-5 h-5 text-purple-600 flex-shrink-0" />
                  ) : (
                    <PlusIcon className="w-5 h-5 text-purple-600 flex-shrink-0" />
                  )}
                </button>
                {openFaq === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="px-6 pb-6"
                  >
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative z-10 py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-violet-600 via-purple-600 to-pink-600 rounded-3xl p-12 text-center shadow-2xl"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Stop Guessing. Start Knowing.
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Join the teams using Joblyn to make every hire more structured, more consistent, and more defensible.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {isAuthenticated ? (
                <button
                  onClick={() => navigate('/dashboard')}
                  className="px-8 py-4 bg-white text-purple-600 font-bold rounded-2xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-200 text-lg"
                >
                  Go to Your Dashboard
                </button>
              ) : (
                <>
                  <button
                    onClick={() => navigate('/login')}
                    className="px-8 py-4 bg-white text-purple-600 font-bold rounded-2xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-200 text-lg"
                  >
                    Request Early Access
                  </button>
                  <a
                    href="mailto:hello@joblyn.ai"
                    className="px-8 py-4 bg-white/20 backdrop-blur-sm text-white font-semibold rounded-2xl hover:bg-white/30 transition-all duration-200 text-lg border border-white/30 inline-flex items-center justify-center"
                  >
                    Talk to Us
                  </a>
                </>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 bg-white/60 backdrop-blur-md border-t border-white/20 py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-3 mb-4 md:mb-0">
              <div className="p-2 bg-gradient-to-br from-violet-600 to-purple-600 rounded-xl shadow-lg">
                <SparklesIcon className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent">
                Joblyn
              </span>
            </div>
            <div className="flex items-center space-x-6 text-sm text-gray-500">
              <div className="flex items-center space-x-2">
                <ShieldCheckIcon className="w-4 h-4 text-green-500" />
                <span>Enterprise-grade security</span>
              </div>
              <span>
                An <a href="https://aiguru.one" target="_blank" rel="noopener noreferrer" className="font-semibold text-purple-600 hover:underline">AI Guru</a> product
              </span>
              <span>&copy; {new Date().getFullYear()} Joblyn. All rights reserved.</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
